
import Throttle from "@/utils/throttle";
import { dateFormat, getAreaDate, numberFormat } from "@/utils/util";
import { defineComponent, reactive, Ref, ref, toRefs } from "vue";
import { DataType, DayType } from "./PurchaseRecord";
import {
  getSourceTypeApi,
  getPurchaseListApi,
  getPurchaseCountApi,
} from "../../../../api/farmer";

function getState(): DataType {
  return {
    showDateModal: false,
    startMinDate: new Date(1900, 0, 1), //开始时间的最小时间限制
    startMaxDate: new Date( // 开始时间的最大时间限制
      dateFormat().YYYY,
      Number(dateFormat().MM) - 1,
      Number(dateFormat().DD)
    ),
    startDate: new Date(
      dateFormat().YYYY,
      Number(dateFormat().MM) - 1,
      Number(dateFormat().DD)
    ),
    endDate: new Date(
      dateFormat().YYYY,
      Number(dateFormat().MM) - 1,
      Number(dateFormat().DD)
    ),
    otherDate: [
      { text: "自定义期间", id: 0 },
      { text: "今天", id: 1 },
      { text: "昨天", id: 2 },
      { text: "本月", id: 3 },
      { text: "本年", id: 4 },
    ],
    selectedDateWay: { text: "本月", id: 3 }, // 默认本月
    showSectionModal: false,
    endMinDate: new Date(1900, 0, 1), // 结束时间的最小时间限制
    endMaxDate: new Date( // 结束时间的最大时间限制
      dateFormat().YYYY,
      Number(dateFormat().MM) - 1,
      Number(dateFormat().DD)
    ),
    showTypeModal: false,
    selectedType: [{ id: -1, name: "全部" }], //
    page: 1,
    pageSize: 10,
    total: 0,
    dataList: [],
    statisticsData: {
      countSn: 0,
      sumNumber: 0,
      sumPrice: "0.00",
    },
    loading: false,
  };
}
export default defineComponent({
  setup() {
    const state = reactive<DataType>(getState());
    const typeList = ref<any>([]);
    const throttle = ref<any>(null);

    const customPicker = {
      text: "name",
      values: "id",
      children: "values",
    };
    const { getSectionDate,onRefresh } = useOper(state);
    getSectionDate();
    const { getSourceTypeData, getStatisticsData, getPurchaseList } =
      useDatas(state);
    getSourceTypeData(typeList);
    getStatisticsData();
    getPurchaseList();
    throttle.value = new Throttle(500, () => {
      // 滚动到底部执行。函数节流
      ++state.page;
      getPurchaseList();
    });
    return {
      ...toRefs(state), // 组件内部状态
      ...useEvent(state, throttle), // 基础事件相关hooks
      ...useFilter(state), // 顶部筛选组件相关hooks
      onRefresh,
      customPicker,
      typeList,
    };
  },
});

// 通过递归对品种数据进行处理
function joinTypeDatas(typeList: Array<any>): Array<any> {
  let tempList: Array<any> = [];

  typeList.forEach((item) => {
    if (item.values.length > 0) {
      tempList.push(item);
      joinTypeDatas(item.values);
    } else {
      delete item.values;
      tempList.push(item);
    }
  });
  typeList.unshift({ id: -typeList[0].level, name: "全部", values: [] });

  return tempList;
}

/**
 * 获取数据模型
 */
const useDatas = (state: DataType) => {
  const { getParams } = useOper(state);
  let params = getParams();
  return {
    // 获取列表数据
    getPurchaseList: async function () {
      const result: any = await getPurchaseListApi({
        cooperationId: params.cooperationId,
        endTime: params.endDate,
        startTime: params.stateDate,
        type: params.type, //第一级类型
        varietyType: params.varietyType, //第二级类型,
        modelType: params.modelType, // 第三级类型
        page: state.page,
        pageSize: state.pageSize,
      });
      if (result.data.code === 0) {
        let data = result.data.data;
        state.total = data.total;
        if (state.page === 1) {
          state.dataList = data.records;
        } else {
          state.dataList = [...state.dataList, ...data.records];
        }
      }
    },
    // 获取统计
    getStatisticsData: async function () {
      const result: any = await getPurchaseCountApi({
        cooperationId: params.cooperationId,
        endTime: params.endDate,
        startTime: params.stateDate,
        type: params.type, //第一级类型
        varietyType: params.varietyType, //第二级类型,
        modelType: params.modelType, // 第三级类型
      });
      if (result.data.code === 0) {
        state.statisticsData = result.data.data;
        state.statisticsData.sumPrice = numberFormat(
          state.statisticsData.sumPrice,
          2
        );
      }
      state.loading = false
    },
    // 获取列表数据
    getSourceTypeData: async (typeList: Ref) => {
      // 获取品种数据
      const result: any = await getSourceTypeApi();
      if (result.data.code === 0) {
        typeList.value = joinTypeDatas(result.data.data);
        typeList.value.unshift({
          id: -1,
          name: "全部",
          values: [
            { id: -2, name: "全部", values: [{ id: -3, name: "全部" }] },
          ],
        });
      }
    },
  };
};

/**
 * 顶部筛选相关事件函数
 */
const useFilter = (state: DataType) => {
  const { getSectionDate } = useOper(state);
  return {
    onTypeConfirm: (e: any) => {
      let tempList = e.filter((item: any) => {
        return item && item.id > 0;
      });
      state.selectedType = tempList.length
        ? tempList
        : [{ name: "全部", id: -1 }];

      const { getPurchaseList, getStatisticsData } = useDatas(state);
      state.page = 1;
      getPurchaseList();
      getStatisticsData();
      state.showTypeModal = false;
    },
    onTypeCancel: () => {
      state.showTypeModal = false;
    },
    onStartDateChange: (e: any) => {
      // 开始时间选择后，结束最小选择时间要相应变化，结束时间不能比开始时间小
      let tempStart = dateFormat(e);
      if (
        new Date(state.startDate).getTime() > new Date(state.endDate).getTime()
      ) {
        state.endMinDate = new Date(
          tempStart.YYYY,
          +tempStart.MM - 1,
          +tempStart.DD
        );
        state.endDate = new Date(
          tempStart.YYYY,
          +tempStart.MM - 1,
          +tempStart.DD
        );
      }
    },
    onReset: () => {
      Object.assign(state, getState());
      getSectionDate();
      const { getPurchaseList, getStatisticsData } = useDatas(state);
      state.page = 1;
      getPurchaseList();
      getStatisticsData();
    },
    onSectionConfirm: (e: DayType) => {
      // 确定选择时间区间
      state.selectedDateWay = e;
      // 根据选择的时间区间重新获取一下对应时间，和开始结束时间相对应
      getSectionDate(Number(e.id));
      const { getPurchaseList, getStatisticsData } = useDatas(state);
      state.page = 1;
      getPurchaseList();
      getStatisticsData();
      state.showSectionModal = false;
    },
    onSectionCancel: () => {
      state.showSectionModal = false;
    },
    onClickWayFilter: () => {
      state.showSectionModal = true;
    },
    transformDates: (date: any, t: number) => {
      let tempDate = dateFormat(new Date(date).getTime());
      return tempDate.YYYY + "-" + tempDate.MM + "-" + tempDate.DD;
    },
    onCancelDate: () => {
      state.showDateModal = false;
    },
    onConfirmDate: () => {
      state.selectedDateWay = state.otherDate[0];
      const { getPurchaseList, getStatisticsData } = useDatas(state);
      state.page = 1;
      getPurchaseList();
      getStatisticsData();
      state.showDateModal = false;
    },
    onClickDateFilter: () => {
      state.showDateModal = true;
    },
    onClickTypeFilter: () => {
      state.showTypeModal = true;
    },
  };
};

/**
 * 操作处理类相关事件
 */
const useOper = (state: DataType) => {
  return {
    // 下拉刷新
    onRefresh: function () {
      const { getPurchaseList, getStatisticsData } = useDatas(state);

      state.loading = true;
      state.page = 1;
      getPurchaseList();
      getStatisticsData();
      
    },
    // 获取列表参数
    getParams: function (): {
      cooperationId: string;
      type: string;
      varietyType: string;
      modelType: string;
      stateDate: string;
      endDate: string;
    } {
      let cooperationId = "";
      const tempData =
        localStorage.getItem("cooperationData") == "undefined" ||
        !localStorage.getItem("cooperationData")
          ? "{}"
          : (localStorage.getItem("cooperationData") as string);
      if (Object.keys(JSON.parse(tempData)).length > 0) {
        cooperationId = JSON.parse(tempData).id;
      } else {
        cooperationId = "";
      }
      const stateDate =
        dateFormat(state.startDate).YYYY +
        "-" +
        dateFormat(state.startDate).MM +
        "-" +
        dateFormat(state.startDate).DD;
      const endDate =
        dateFormat(state.endDate).YYYY +
        "-" +
        dateFormat(state.endDate).MM +
        "-" +
        dateFormat(state.endDate).DD;
      // 获取类型参数
      let type =
        state.selectedType[0] && state.selectedType[0].id > 0
          ? state.selectedType[0].id
          : "";
      let varietyType =
        state.selectedType[1] && state.selectedType[1].id > 0
          ? state.selectedType[1].id
          : "";
      let modelType =
        state.selectedType[2] && state.selectedType[2].id > 0
          ? state.selectedType[2].id
          : "";
      return {
        cooperationId,
        type,
        varietyType,
        modelType,
        stateDate,
        endDate,
      };
    },
    // 获取时间区间对应的时间
    getSectionDate: (id: number = 3) => {
      let temp = getAreaDate(id);
      if (temp) {
        let tempDate = temp.split("~");
        //
        state.startDate = new Date(tempDate[0]);
        state.endMaxDate = new Date(tempDate[1]);
        state.endDate = new Date(tempDate[1]);
        // 设置最小结束时间，结束时间的选择不能小于开始时间
        state.endMinDate = state.startDate;
      }
    },
  };
};

/**
 * 基础事件相关函数
 */
const useEvent = (state: DataType, throttle: any) => {
  return {
    onLoad: () => {
      if (state.total === state.dataList.length) {
        return;
      }
      throttle.value.search(null);
    },
  };
};
